<template>
  <v-layout>
    <v-combobox
      multiple
      class="combobox-drop-down-container"
      v-bind="$attrs"
      v-on="$listeners"
      :hide-selected="isHideSelected"
      :items="items"
      :delimiters="delimiters"
      :readonly="isMaxSelectionsReached"
      :hint="hint"
      persistent-hint
      :hide-details="!isMaxSelectionsReached || !hint"
      :disabled="disabled"
      :class="{
        required: isRequired,
        'select-drop-down': true,
        'custom-selection': !!$scopedSlots.selection,
        'has-value': !!$attrs.value,
        'flex-wrap': isFlexWrap,
        'tall': isTall,
      }"
      :menu-props="menuProps"
      item-color="rgb(84, 210, 140)"
      @input="selected => selections = selected"
      :style="{color: `${brandBgColor} !important`}"
      :data-qa="`${dataQa}-combobox`">
      <template v-if="useSelectionSlot" #selection="selectionObj">
        <slot v-bind="selectionObj" name="selection"/>
      </template>

      <template v-if="!!tooltip" #append>
        <vc-layout align-center class="combobox-drop-down-container__tooltip">
          <vc-icon-with-tooltip :data-qa="`${dataQa}-tooltip`"
                                v-bind="tooltip">
          </vc-icon-with-tooltip>
        </vc-layout>
      </template>
    </v-combobox>
  </v-layout>
</template>

<script>
import ClickOutside from 'vue-click-outside'
export default {
  name: "VcitaCombobox",
  props: {
    items: {
      type: Array,
    },
    delimiters: {
      type: [Array, String]
    },
    useSelectionSlot: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isFlexWrap: {
      type: Boolean,
      default: false
    },
    isHideSelected: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
    },
    maxSelected: {
      type: Number,
    },
    dataQa: {
      type: String,
      default: 'VcitaCombobox'
    }
  },
  directives: {
    ClickOutside
  },
  data() {
    return {
      isTall: false,
      selections: [],
    }
  },
  computed: {
    isRequired() {
      return [false, undefined].indexOf(this.$attrs.required) === -1
    },
    isMaxSelectionsReached() {
      return !isNaN(this.maxSelected) && this.selections.length >= this.maxSelected
    },
    brandBgColor() {
      return this.$store && this.$store.getters['brandModule/secondaryColor'];
    },
    menuProps() {
      let contentClass = 'vcita-combobox-menu'
      if (this.$attrs['menu-props']) {
        if (this.$attrs['menu-props'].contentClass) {
          contentClass += this.$attrs['menu-props'].contentClass
        }
      }
      let selectionsReached = {}
      if (this.isMaxSelectionsReached) {
        selectionsReached = {
          'open-on-click': true,
          'value': false,
        }
      }
      return {
        ...this.$attrs['menu-props'],
        ...selectionsReached,
        'close-on-click': true,
      }
    },
  },
  methods: {
    onResize() {
      this.isTall = this.$el.offsetHeight > 60;
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(this.onResize)
    this.resizeObserver.observe(this.$el)
  },
  beforeDestroy() {
    this.resizeObserver.unobserve(this.$el)
  }
}
</script>

<style lang="scss" scoped>
.combobox-drop-down-container {
  position: relative;
  font-family: var(--primary-font-family);
  border-radius: 6px;

  &.error--text {
    ::v-deep {
      .v-input__slot {
        border: 1px solid #F2514A;
      }
    }
  }

    &.select-drop-down.v-input--is-disabled {
      ::v-deep {
        .v-input__slot {
          background-color: #F7F7F7 !important;
          .v-label--is-disabled {
            color: #757575 !important;
          }
        }
      }
    }

  ::v-deep.v-text-field__details {
    margin-top: 4px
  }

  ::v-deep.v-input__slot {
    margin-bottom: 0px;

    &:hover {
      border: 1px solid var(--gray-darken-3);
    }

    .v-input__icon--append {
      i {
        color: var(--gray-darken-3) !important;
      }
    }
  }

  ::v-deep.v-label--active {
    font-weight: 400;
    font-size: 11px;
    color: #616161 !important;
  }

  ::v-deep.v-select__selections {
    max-width: 90%;
    flex-wrap: nowrap;
  }

  &.flex-wrap {
    ::v-deep {
      .v-label--active {
        top: 24px;
      }

      .v-select__selections {
        flex-wrap: wrap;
        margin-top: 24px;
      }

      .v-input__append-inner {
        height: 100%;
      }
    }
  }

  &__tooltip:before {
    content: '';
    border-left: 1px solid #E0E0E0;
    margin-inline-end: 12px;
    margin-block-start: 3px;
    height: 36px;
  }

  &.tall {
    .combobox-drop-down-container__tooltip:before {
      height: 100%;
    }
  }
}


</style>

<style lang="scss">
.vcita-combobox-menu {
  border-radius: 5px !important;

  .v-list-item__action {
    margin: 1px 2px 1px 1px !important;
  }

  .v-list-item {
    min-height: 37px !important;
  }

  .theme--light.v-list-item--active:hover::before, .theme--light.v-list-item--active::before {
    opacity: 0;
  }

  .v-icon {
    font-size: 20px !important;
  }

  .v-list-item__title {
    font-size: 13px;
    color: black;
    font-family: var(--primary-font-family);
    font-weight: 500;
  }

  .v-subheader {
    color: rgba(0, 0, 0, 0.54);
    font-family: var(--primary-font-family);
    font-weight: 600;
    font-size: 11px;
    height: 32px;

    & ~ .v-subheader {
      border-top-style: solid;
      border-top-width: 1px;
      border-top-color: rgba(0, 0, 0, 0.12);
      padding-top: 6px;
      margin-top: 6px;
    }
  }
}

</style>
